import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FilterRangeSelector from 'ui-component/filter-range/FilterRangeSelector';
import moment from 'moment';
import { Skeleton, Tooltip, Avatar } from '@mui/material';
import { FaFilter } from 'react-icons/fa6';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import { PiInfoFill, PiMicrosoftExcelLogoFill, PiMicrosoftExcelLogo, PiFilePdf } from 'react-icons/pi';
import { MdKeyboardArrowDown, MdOutlineSettingsBackupRestore } from 'react-icons/md';
import * as XLSX from 'xlsx';
import usePageTitle from 'hooks/usePageTitle';
import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import useIsMobile from 'utils/isMobileUtils';
import { getCurrencySymbol, getCurrencySymbolV2, numberFormatter } from 'utils/currency';
import { FaSpinner, FaTimes } from 'react-icons/fa';

const ProfitLossShippingCostsStatement = () => {
    const [setPageTitle] = usePageTitle('Profit and Loss Statement');
    const statusTypes = useSelector((state) => state.main.statusTypes);
    const statusTypeList = statusTypes?.map((item) => ({ id: item.id, name: item.name }));
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);

    const [showFilterDateUI, setShowFilterDateUI] = useState(false);

    const [dataList, setDataList] = useState([]);
    const tableRef = useRef();


    const [selectedFilterBy, setSelectedFilterBy] = useState('MONTH');
    const [defaultSelectedStartYear, setDefaultSelectedStartYear] = useState(0);
    const [defaultSelectedEndYear, setDefaultSelectedEndYear] = useState(0);
    const [defaultSelectedStartMonth, setDefaultSelectedStartMonth] = useState(0);
    const [defaultSelectedEndMonth, setDefaultSelectedEndMonth] = useState(0);

    const [searchParams] = useSearchParams();

    
    useEffect(() => {
        const defaultFilterBy = searchParams.get('filterBy') || 'MONTH';
        const defaultStartYear = parseInt(searchParams.get('startYear')) || (moment().year());
        const defaultStartMonth = parseInt(searchParams.get('startMonth')) || 1;
        const defaultEndYear = parseInt(searchParams.get('endYear')) || moment().year();
        const defaultEndMonth = parseInt(searchParams.get('endMonth')) || (moment().month() + 1);
        setSelectedFilterBy(defaultFilterBy);
        setDefaultSelectedStartYear(defaultStartYear);
        setDefaultSelectedStartMonth(defaultStartMonth);
        setDefaultSelectedEndYear(defaultEndYear);
        setDefaultSelectedEndMonth(defaultEndMonth);
    
        // processReport(moment().year(), defaultEndYear, '1', defaultEndMonth, defaultFilterBy);
        processReport(defaultStartYear, defaultEndYear, defaultStartMonth, defaultEndMonth, defaultFilterBy);
    }, [searchParams])

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const openInNewTab = (url) => {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
    };

    const viewDetails = (id) => {
        openInNewTab(`/shipments/${id}`);
    };

    const isMobile = useIsMobile();

    const [total, setTotal] = useState({
        eur: 0,
        usd: 0,
        aed: 0,
        all: 0
    });

    const processReport = (startYear, endYear, startMonth, endMonth, filterBy) => {
        setIsLoading(true);
        const request = {
            filter_by: filterBy,
            start_year: startYear,
            end_year: endYear,
            start_month: startMonth,
            end_month: endMonth
        }
        
        api.generateProfitLossShippingCostsStatementReport(request).then((res) => {
            setIsLoading(false);
            if (res?.status) {
                // setReportData(res.data); 
                const overAllAmountEur = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.eur || 0), 0);
                const overAllAmountUsd = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.usd || 0), 0);
                const overAllAmountAed = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.aed || 0), 0);
                const overAllAmountAll = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.all || 0), 0);
                
                setTotal({
                    ...total,
                    eur: overAllAmountEur,
                    usd: overAllAmountUsd,
                    aed: overAllAmountAed,
                    all: overAllAmountAll
                });
                setDataList(res?.data)
            }
        }).catch((error) => {
            setIsLoading(false);
            console.error('Error fetching Profit and Loss Statement Report:', error);
        });

    }

    const formatMonth = (date) => date.toLocaleString("en-US", { month: "short", year: "numeric" });

    const formatQuarter = (date) => {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter} ${date.getFullYear()}`;
    };

    const formatYear = (date) => date.getFullYear();
    const dateFilterTextValue = useMemo(() => {
        if (!defaultSelectedStartYear || !defaultSelectedEndYear || !defaultSelectedStartMonth || !defaultSelectedEndMonth) return "";
        
        const startDate = new Date(defaultSelectedStartYear, defaultSelectedStartMonth, 0);
        const endDate = new Date(defaultSelectedEndYear, defaultSelectedEndMonth, 0);
        switch (selectedFilterBy) {
            case "MONTH":
            return `${formatMonth(startDate) === formatMonth(endDate) ? formatMonth(startDate) : `${formatMonth(startDate)} - ${formatMonth(endDate)}`}`;

            case "QUARTER":
            return `${formatQuarter(startDate)} - ${formatQuarter(endDate)}`;

            case "YEAR":
                const yearValue = defaultSelectedStartYear === defaultSelectedEndYear ? formatYear(startDate) : `${formatYear(startDate)} - ${formatYear(endDate)}`;
            return yearValue;

            default:
            return "";
        }
    }, [selectedFilterBy, defaultSelectedStartYear, defaultSelectedEndYear, defaultSelectedStartMonth, defaultSelectedEndMonth]);
    


    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    };

    const exportReportXlsx = () => {
        const request = {
            filter_by: selectedFilterBy,
            start_year: defaultSelectedStartYear,
            end_year: defaultSelectedEndYear,
            start_month: defaultSelectedStartMonth,
            end_month: defaultSelectedEndMonth,
            type: 'excel', 
            currency: selectedCurrency === 'all' ? 'usd' : selectedCurrency
        };

        setIsExporting(true);
        api.downloadProfitLossShippingCostsStatementReport(request).then((res) => {
            setIsExporting(false);

            const wb = XLSX.utils.book_new();
            // Create a worksheet
            const ws = XLSX.utils.json_to_sheet(res?.data);
            XLSX.utils.book_append_sheet(wb, ws, 'Profit & Loss - Shipping');

            // Convert the workbook to a binary string
            const wbBinaryString = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });

            // Convert the binary string to a Blob
            const blob = new Blob([s2ab(wbBinaryString)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            saveAs(blob, `Projectmix Profit and Loss Statement - Shipping Costs.xlsx`);
 
        });
    };


    return (
        <div className="row justify-content-center m-0">
                
            <div className="medium-sized-container" style={{ maxWidth: '900px' }}>
                <div className="col-xl-12 col-lg-12 p-0">
                    <div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
                        <div className="header-bubble" />
                        <div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
                            <h5 style={{ textAlign: 'center' }}>Profit and Loss Statement - Shipping Costs</h5>
                        
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                <button
                                    className="btn btn-primary btn-outlined-main white-var"
                                    onClick={() => {
                                        if (!isLoading) {
                                            setShowFilterDateUI(!showFilterDateUI)
                                        }
                                    }}
                                >
                                    <span className="menuIcon">
                                        <FaFilter style={{ fontSize: '1em' }} />
                                    </span>
                                    <span>Filter</span>
                                </button>
                                <button
                                    className={`btn btn-primary btn-outlined-main white-var ${isExporting ? 'disabled' : ''}`}
                                    onClick={() => {
                                        if (!isExporting) {
                                            exportReportXlsx();
                                        }
                                    }}
                                    disabled={isExporting}
                                >
                                    {
                                        isExporting ? (
                                            <>
                                                <span className="menuIcon">
                                                    <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                </span>
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </>
                                        )
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                    {
                        isMobile && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 10,
                                    paddingTop: 0,
                                    paddingBottom: 5,
                                    paddingLeft: 20,
                                    paddingRight: 20
                                }}
                                className="show-on-mobile"
                            >
                                {
                                    showFilterDateUI && (
                                        <FilterRangeSelector
                                            showFilter={showFilterDateUI}
                                            defaultSelectedStartYear={defaultSelectedStartYear}
                                            defaultSelectedEndYear={defaultSelectedEndYear}
                                            defaultSelectedStartMonth={defaultSelectedStartMonth}
                                            defaultSelectedEndMonth={defaultSelectedEndMonth}
                                            onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                                setDefaultSelectedStartYear(startYear);
                                                setDefaultSelectedEndYear(endYear);
                                                setDefaultSelectedStartMonth(startMonth);
                                                setDefaultSelectedEndMonth(endMonth);
                                                setSelectedFilterBy(filterBy);
                                                setShowFilterDateUI(false);
                                                processReport(startYear, endYear, startMonth, endMonth, filterBy);
                                                // Apply your filtering logic here based on selected dates
                                            }}
                                            showFilterBy={selectedFilterBy}
                                            onFilterVisibility={() => {
                                                setShowFilterDateUI(false);
                                            }}
                                        />
                                    )
                                }
                            </div>
                        )
                    }
                    <div className="section-head-new pl-0 hide-on-mobile" style={{ margin: '10px 0' }}>
                        <div>
                            <button
                                className={`btn btn-primary btn-outlined-main ${isLoading ? 'disabled' : ''}`}
                                style={{
                                    gap: '0',
                                    zIndex: '12'
                                }}
                                onClick={() => {
                                    if (!isLoading) {
                                        setShowFilterDateUI(!showFilterDateUI)
                                    }
                                }}
                            >
                                Shipping Costs for {dateFilterTextValue}
                                <span className={`button-rotating-arrow ${showFilterDateUI ? 'rotate-180' : ''} `}>
                                    <MdKeyboardArrowDown />
                                </span>
                            </button>
                            {
                                showFilterDateUI && (
                                    <FilterRangeSelector
                                        showFilter={showFilterDateUI}
                                        defaultSelectedStartYear={defaultSelectedStartYear}
                                        defaultSelectedEndYear={defaultSelectedEndYear}
                                        defaultSelectedStartMonth={defaultSelectedStartMonth}
                                        defaultSelectedEndMonth={defaultSelectedEndMonth}
                                        onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                            setDefaultSelectedStartYear(startYear);
                                            setDefaultSelectedEndYear(endYear);
                                            setDefaultSelectedStartMonth(startMonth);
                                            setDefaultSelectedEndMonth(endMonth);
                                            setSelectedFilterBy(filterBy);
                                            setShowFilterDateUI(false);
                                            processReport(startYear, endYear, startMonth, endMonth, filterBy);
                                            // Apply your filtering logic here based on selected dates
                                        }}
                                        showFilterBy={selectedFilterBy}
                                        onFilterVisibility={() => {
                                            setShowFilterDateUI(false);
                                        }}
                                    />
                                )
                            }
                        </div>
                        <div className="actions">
                            
                            {isLoading ? (
                                    <Dropdown
                                        className="btnDel est-btn iconbtn disabled"
                                        align={'end'}
                                        disabled
                                    >
                                        <Dropdown.Toggle as="span">
                                            <Link
                                                to={''}
                                                className="btn btn-primary btn-outlined-main white-var disabled" disabled
                                                style={{ minWidth: 'unset', gap: '5px' }}
                                            >
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </Link>
                                        </Dropdown.Toggle>
                                    </Dropdown>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isExporting) {
                                            exportReportXlsx();
                                        }
                                    }}
                                    className={`btn btn-primary btn-outlined-main ${isExporting ? 'disabled' : ''}`}
                                    disabled={isExporting}
                                >
                                        
                                    {
                                        isExporting ? (
                                            <>
                                                <span className="menuIcon">
                                                    <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                </span>
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </>
                                        )
                                    }
                                </button>
                            )}
                        </div>
                    </div>
                    <div
                        className={`card shadow-sm  bg-white mb-4 table-card`}
                        style={{
                            height: `${isMobile ? 'calc(100vh - 250px)' : 'auto'}`,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        
                        <div className="estimation-table-area">
                            <div className="table-side">

                                {
                                    !isMobile ? (
                                        <>
                                            <div
                                                className="table-responsive-xl tableData estimationList"
                                                ref={tableRef}
                                                style={{
                                                    height: 'calc(100vh - 200px)',
                                                    overflowY: 'auto',
                                                    scrollbarWidth: 'thin',
                                                    scrollbarColor: '#0966ff transparent'

                                                }}
                                            >
                                                    <table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
                                                        <thead>
                                                            <tr className="sticky-header">
                                                                
                                                                <th scope="col">Date</th>
                                                                <th scope="col">Tracking</th>
                                                                <th scope="col">Invoice</th>
                                                                <th scope="col">Receiver</th>
                                                                <th scope="col">Courier</th>
                                                                <th scope="col">Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {isLoading ? (
                                                                <>
                                                                    {[...Array(50)].map((e, i) => (
                                                                        <tr style={{ height: '35px' }} key={i}>
                                                                            {[...Array(6)].map((e, i) => (
                                                                                <td style={{ height: '35px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {dataList?.length > 0 && (
                                                                        dataList?.map((item, index) => {
                                                                            return (
                                                                                <tr key={index}>
                                                                                
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            height: '35px'
                                                                                        }}
                                                                                    >
                                                                                        {`${moment(item?.date).format(CommonDateOnlyFormat)}`}
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            height: '35px',
                                                                                            maxWidth: '150px',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap',
                                                                                        }}
                                                                                    >
                                                                                        <a
                                                                                            href={`/shipments/${item.id}`}
                                                                                            target="_blank"
                                                                                            style={{
                                                                                                textDecoration: 'none',
                                                                                                display: 'inline-block',
                                                                                                maxWidth: '150px',
                                                                                                overflow: 'hidden',
                                                                                                textOverflow: 'ellipsis',
                                                                                                whiteSpace: 'nowrap',
                                                                                                verticalAlign: 'middle'
                                                                                            }}
                                                                                            title={item?.tracking_no}
                                                                                        >
                                                                                            {item?.tracking_no && item.tracking_no.length > 150
                                                                                                ? item.tracking_no.substring(0, 150) + '...'
                                                                                                : item.tracking_no}
                                                                                        </a>
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        // onClick={(e) => {
                                                                                        //     viewDetails(item.id);
                                                                                        // }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            height: '35px'
                                                                                        }}
                                                                                    >
                                                                                        <a
                                                                                            href={`/sales/invoices/${item?.invoice?.id}`}
                                                                                            target="_blank"
                                                                                            style={{
                                                                                                textDecoration: 'none',
                                                                                            }}
                                                                                        >
                                                                                            { item?.invoice?.invoice_number }
                                                                                        </a>
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            height: '35px'
                                                                                        }}
                                                                                    >
                                                                                        { item?.receiver }
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            height: '35px'
                                                                                        }}
                                                                                    >
                                                                                        { item?.courier }
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            height: '35px'
                                                                                        }}
                                                                                    >
                                                                                        <div className="flex gap-1">
                                                                                            <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, item?.currency, '11px', '11px')}
                                                                                            </div>
                                                                                            <div>
                                                                                                {item?.converted_currency_total ? numberFormatter.format(item?.converted_currency_total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : '0.00'}
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })
                                                                    )}
                                                                    {(dataList?.length < 1 && !isLoading) && (
                                                                        <tr className="no-hover">
                                                                            <td colSpan={6}>
                                                                                <div className="not-found-container">
                                                                                    <div className="not-found-icon">
                                                                                        <img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} />
                                                                                    </div>
                                                                                    <h6 className="not-found-text">No Data Available</h6>
                                                                                    
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </>
                                                            )}
                                                        </tbody>
                                                </table>
                                            </div>
                                            <div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px', justifyContent: 'space-between', padding: '10px 15px 10px 10px' }}>
                                                <div className="fw-500" style={{ fontSize: '16px' }}>Total</div>
                                                <div className="fw-500" style={{ fontSize: '16px' }}>
                                                    {isLoading ? (
                                                        <div style={{ width: '100px' }}>
                                                            <Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-1">
                                                            <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                            </div>
                                                            <div>
                                                                {total ? numberFormatter.format(total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : 'N/A'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        isLoading ? (
                                            <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                                {[...Array(15)].map((_, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="order-list-card"
                                                        style={{
                                                            background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                            marginBottom: 10,
                                                            borderRadius: 12,
                                                            padding: 16,
                                                            minHeight: 90,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 10
                                                        }}
                                                    >
                                                        {[...Array(3)].map((__, i) => (
                                                            <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="inv-cards-list d-md-none">
                                            
                                                {dataList?.length > 0 ? (
                                                    dataList.map((item, index) => (
                                                        
                                                        <div
                                                            className="order-list-card active-action"
                                                            style={{
                                                                background: index % 2 === 0 ? '#f9f9f9' : '#fff'
                                                            }}
                                                            onClick={() => viewDetails(item?.id)}
                                                        >
                                                            <div className="order-list-card-header">
                                                                <h6
                                                                    className="order-no text-primary"
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        maxWidth: '200px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            textDecoration: 'none',
                                                                            display: 'inline-block',
                                                                            maxWidth: '200px',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            verticalAlign: 'middle'
                                                                        }}
                                                                        title={item?.tracking_no}
                                                                        
                                                                    >

                                                                        {item?.tracking_no && item.tracking_no.length > 200
                                                                            ? item.tracking_no.substring(0, 200) + '...'
                                                                            : item.tracking_no}
                                                                    </span>
                                                                </h6>
                                                                <p className="order-date">{moment(item?.date).format(CommonDateOnlyFormat)}</p>
                                                            </div>

                                                            <div className="order-list-card-body">
                                                                <div className="order-contacts">
                                                                    <p className="order-customer">
                                                                        { item?.invoice?.invoice_number }
                                                                    </p>
                                                                    <p className="order-customer">
                                                                        { item?.receiver }
                                                                    </p>
                                                                </div>
                                                                <p className="total-val">

                                                                    <div className="flex gap-1">
                                                                        <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                            {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, item?.currency, '11px', '11px')}
                                                                        </div>
                                                                        <div>
                                                                            {item?.converted_currency_total ? numberFormatter.format(item?.converted_currency_total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : '0.00'}
                                                                        </div>
                                                                    </div>
                                                                    {/* <CurrencyValue currencyObject={item?.converted_total_price} defaultCurrency={item?.currency} /> */}
                                                                </p>
                                                            </div>

                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12 }}>
                                                        <img src={NoRecordFoundIcon} style={{ height: '120px', width: '120px' }} />
                                                        <div style={{ fontStyle: 'italic', color: '#000', fontSize: '16px', marginTop: 12 }}>No Data Available</div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )
                                }
                                {isLoading ? (
                                    <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                        {[...Array(15)].map((_, idx) => (
                                            <div
                                                key={idx}
                                                className="order-list-card"
                                                style={{
                                                    background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                    marginBottom: 10,
                                                    borderRadius: 12,
                                                    padding: 16,
                                                    minHeight: 90,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 10
                                                }}
                                            >
                                                {[...Array(3)].map((__, i) => (
                                                    <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        {/* <Pagination pagination={paginationData} data={handleData} onLoading={(e) => {}} reactQuery /> */}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    {
                        isMobile && (
                            <div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px', justifyContent: 'space-between', padding: '10px 25px 10px 25px' }}>
                                <div className="fw-500" style={{ fontSize: '16px' }}>Total</div>
                                <div className="fw-500" style={{ fontSize: '16px' }}>
                                    {isLoading ? (
                                        <div style={{ width: '100px' }}>
                                            <Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
                                        </div>
                                    ) : (
                                        <div className="flex gap-1">
                                            <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                            </div>
                                            <div>
                                                {total ? numberFormatter.format(total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : 'N/A'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default ProfitLossShippingCostsStatement;
